import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ArrowRight, ShieldCheck, MessageSquareWarning, PhoneCall, FolderKanban, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import DKFooter from "@/components/DKFooter";
import LanguageThemeSelector from "@/components/LanguageThemeSelector";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accessCode, setAccessCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-login from QR code scan (?code=...)
  useEffect(() => {
    const codeFromUrl = searchParams.get("code");
    if (codeFromUrl && !user && !loading) {
      setAccessCode(codeFromUrl);
      // Strip query param immediately so it doesn't hang around in history
      setSearchParams({}, { replace: true });
      (async () => {
        setLoading(true);
        const res = await login(codeFromUrl.trim());
        setLoading(false);
        if (res.ok) {
          toast.success("Connexion réussie via QR");
          navigate("/");
        } else {
          setError(res.error || "Code d'accès invalide");
        }
      })();
    }
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(accessCode.trim());
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion réussie");
      navigate("/");
    } else {
      setError(res.error || "Code d'accès invalide");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <LanguageThemeSelector variant="floating" className="!top-3 !left-3 !right-auto md:!right-auto md:!left-3" />

      {/* Hero side */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden login-hero text-white">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-white/95 backdrop-blur-md grid place-items-center border border-white/20 shadow-lg overflow-hidden p-1.5" data-testid="login-brand-logo">
            <img src="/dkconnect-logo.png" alt="DKCONNECT" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">DK CONNECT</div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-blue-200">{t("brand.tagline")}</div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-blue-200 mb-3">{t("auth.unified")}</div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
              {t("auth.pilot_head")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-violet-200">{t("auth.pilot_accent")}</span>{t("auth.pilot_tail")}
            </h1>
            <p className="mt-5 text-blue-100/85 text-[15px] max-w-lg">{t("auth.pilot_sub")}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[
              { icon: FolderKanban, label: t("auth.feat_dossiers"), tone: "from-blue-500/25 to-blue-500/5" },
              { icon: MessageSquareWarning, label: t("auth.feat_reclam"), tone: "from-orange-500/25 to-orange-500/5" },
              { icon: PhoneCall, label: t("auth.feat_appels"), tone: "from-emerald-500/25 to-emerald-500/5" },
              { icon: ShieldCheck, label: t("auth.feat_ged"), tone: "from-violet-500/25 to-violet-500/5" },
            ].map((f) => (
              <div key={f.label} className={`rounded-xl border border-white/15 px-4 py-3 bg-gradient-to-br ${f.tone} backdrop-blur-sm`}>
                <f.icon className="w-4 h-4 text-white/90" strokeWidth={1.75} />
                <div className="mt-2 text-sm font-medium">{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-blue-200/70 space-y-1">
          <div>© {new Date().getFullYear()} DK CONNECT — {t("common.rights_reserved")}</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-blue-200/60">
            {t("brand.developed_by")} <span className="text-blue-100/90 font-semibold">DKCONNECT</span>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="mx-auto lg:mx-0 w-14 h-14 rounded-2xl bg-blue-900 text-white grid place-items-center mb-5">
              <KeyRound className="w-7 h-7" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-blue-800 dark:text-blue-300 mb-2">{t("auth.workspace")}</div>
            <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">{t("auth.welcome")}</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
              Saisissez votre <b>code d&apos;accès personnel</b> fourni par votre administrateur.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="access-code" className="text-slate-700 dark:text-slate-200">Code d&apos;accès</Label>
              <div className="relative">
                <Input
                  id="access-code"
                  type={showCode ? "text" : "password"}
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  autoComplete="off"
                  autoFocus
                  placeholder="Ex : RyMA3HM7HYYs"
                  required
                  data-testid="login-access-code-input"
                  className="h-11 pr-10 font-mono tracking-wider"
                />
                <button
                  type="button"
                  onClick={() => setShowCode((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  aria-label={showCode ? "Masquer le code" : "Afficher le code"}
                >
                  {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-400">
                Vous avez perdu votre code ? Contactez votre administrateur pour en régénérer un.
              </p>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !accessCode.trim()}
              className="w-full h-11 bg-blue-900 hover:bg-blue-800 rounded-full text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? t("auth.connecting") : (
                <span className="inline-flex items-center gap-2">{t("auth.submit")} <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          <div className="mt-10">
            <div className="text-center text-sm text-slate-500 dark:text-slate-400 mb-4">
              Vous êtes une entreprise et souhaitez essayer DKConnect ?{" "}
              <a href="/inscription" className="text-blue-800 dark:text-blue-300 hover:underline font-medium" data-testid="login-to-inscription">
                Demander un accès
              </a>
            </div>
            <DKFooter variant="compact" />
          </div>
        </div>
      </div>
    </div>
  );
}
